import { useState, useEffect, useCallback, useRef } from 'react';
import { ordersApi } from '../services/api';
import usePageVisibility from './usePageVisibility';
import { POLLING_INTERVAL } from '../utils/polling';

/**
 * Hook personnalisé pour gérer les notifications de messages non lus
 * Implémente le polling automatique avec pause quand la page n'est pas visible
 *
 * @returns {Object} - { unreadCounts, loading, error, loadUnreadCounts, startPolling, stopPolling }
 */
const useNotifications = () => {
    const [unreadCounts, setUnreadCounts] = useState({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const pollingIntervalId = useRef(null);
    const isVisible = usePageVisibility();
    const protectedOrderIds = useRef(new Set()); // IDs protégés contre l'écrasement

    /**
     * Charge les compteurs de messages non lus pour toutes les commandes
     * @param {Array} orders - Liste des commandes
     * @param {string} userType - Type d'utilisateur ('client' ou 'prosthetist')
     */
    const loadUnreadCounts = useCallback(async (orders, userType) => {
        if (!orders || orders.length === 0 || !userType) {
            return;
        }

        try {
            setLoading(true);
            setError(null);

            // Charger les compteurs pour toutes les commandes en parallèle
            const countsPromises = orders.map(async (order) => {
                try {
                    const count = await ordersApi.getUnreadMessagesCount(order.id, userType);
                    return { orderId: order.id, count };
                } catch (err) {
                    console.error(`Erreur lors du chargement du compteur pour la commande ${order.id}:`, err);
                    return { orderId: order.id, count: 0 };
                }
            });

            const results = await Promise.all(countsPromises);

            // Merger intelligemment les nouveaux counts avec les existants
            // Ne pas écraser les IDs protégés (optimistic updates)
            setUnreadCounts(prev => {
                const merged = { ...prev };

                results.forEach(({ orderId, count }) => {
                    // Si cet order est protégé et déjà à 0, ne pas écraser
                    if (protectedOrderIds.current.has(orderId) && prev[orderId] === 0) {
                        // Garder la valeur optimistic (0)
                        // Ne rien faire
                    } else {
                        // Mettre à jour avec la valeur du serveur
                        merged[orderId] = count;

                        // Si le serveur confirme 0, retirer de la protection
                        if (count === 0) {
                            protectedOrderIds.current.delete(orderId);
                        }
                    }
                });

                return merged;
            });
        } catch (err) {
            console.error('Erreur lors du chargement des compteurs de messages non lus:', err);
            setError('Impossible de charger les notifications');
        } finally {
            setLoading(false);
        }
    }, []);

    /**
     * Démarre le polling automatique
     * @param {Array} orders - Liste des commandes
     * @param {string} userType - Type d'utilisateur
     * @param {number} interval - Intervalle de polling en ms (défaut: POLLING_INTERVAL)
     */
    const startPolling = useCallback((orders, userType, interval = POLLING_INTERVAL) => {
        // Arrêter le polling existant si présent
        stopPolling();

        // Fonction de polling qui vérifie la visibilité de la page
        const pollFn = () => {
            if (isVisible) {
                loadUnreadCounts(orders, userType);
            }
        };

        // Charger immédiatement
        loadUnreadCounts(orders, userType);

        // Démarrer le polling
        pollingIntervalId.current = setInterval(pollFn, interval);
    }, [isVisible, loadUnreadCounts]);

    /**
     * Arrête le polling automatique
     */
    const stopPolling = useCallback(() => {
        if (pollingIntervalId.current) {
            clearInterval(pollingIntervalId.current);
            pollingIntervalId.current = null;
        }
    }, []);

    /**
     * Obtenir le compteur de messages non lus pour une commande spécifique
     * @param {number} orderId - ID de la commande
     * @returns {number} - Nombre de messages non lus
     */
    const getUnreadCount = useCallback((orderId) => {
        return unreadCounts[orderId] || 0;
    }, [unreadCounts]);

    /**
     * Mettre à jour manuellement le compteur d'une commande (pour optimistic updates)
     * @param {number} orderId - ID de la commande
     * @param {number} count - Nouveau compteur
     */
    const setUnreadCount = useCallback((orderId, count) => {
        setUnreadCounts(prev => ({
            ...prev,
            [orderId]: Math.max(0, count) // Ne jamais descendre en dessous de 0
        }));
    }, []);

    /**
     * Décrémenter le compteur d'une commande (optimistic update quand on marque comme lu)
     * @param {number} orderId - ID de la commande
     */
    const decrementUnreadCount = useCallback((orderId) => {
        // Protéger cet ID contre l'écrasement pendant 5 secondes
        protectedOrderIds.current.add(orderId);

        // Retirer la protection après 5 secondes (temps suffisant pour l'API)
        setTimeout(() => {
            protectedOrderIds.current.delete(orderId);
        }, 5000);

        setUnreadCounts(prev => ({
            ...prev,
            [orderId]: 0 // Quand on ouvre une commande, on marque tout comme lu
        }));
    }, []);

    // Cleanup au unmount
    useEffect(() => {
        return () => {
            stopPolling();
        };
    }, [stopPolling]);

    return {
        unreadCounts,
        loading,
        error,
        loadUnreadCounts,
        startPolling,
        stopPolling,
        getUnreadCount,
        setUnreadCount,
        decrementUnreadCount
    };
};

export default useNotifications;
